import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getScheduledTransfers,
  createScheduledTransfer,
  updateScheduledTransfer
} from '@store/actions/libraDashboardActions';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import { updateLibraScheduledTransfersReqData } from '@store/actions/reqDataActions';
import Pagination from '@components/Pagination';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DataTable from '@components/DataTable';
import { MdAdd, MdEdit } from 'react-icons/md';
import { INPUT_FIELD_TYPE, INPUT_TYPE } from '@components/DynamicFormField/constant';
import { getScheduledTransfersColumns } from './tableColumns';
import { getScheduledTransferFields } from './formConfigs';
import { buildScheduledTransferRequestData } from './utils';

export const ScheduledTransfersTab = ({ selectedItem, onItemClick }) => {
  const dispatch = useDispatch();
  const { hasAuthority } = useAuthRole();
  const isBackofficeManager = hasAuthority(ROLES.BACKOFFICE_MANAGER_ROLE);

  // Redux state
  const scheduledTransfers = useSelector((state) => state.libraDashboard.scheduledTransfers);
  const createScheduledTransferState = useSelector((state) => state.libraDashboard.createScheduledTransfer);
  const updateScheduledTransferState = useSelector((state) => state.libraDashboard.updateScheduledTransfer);
  const libraConfigs = useSelector((state) => state.libraDashboard.libraConfigs);
  const reqData = useSelector((state) => state.reqData.libraDashboard.scheduledTransfers);

  // Local state
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState([]);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [pendingScheduledTransferId, setPendingScheduledTransferId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'create' or 'edit'

  const {
    isOpen: isCreateModalOpen,
    onClose: closeCreateModal,
    onOpen: openCreateModal
  } = useDisclosure();

  const {
    isOpen: isEditModalOpen,
    onClose: closeEditModal,
    onOpen: openEditModal
  } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onClose: closeConfirmModal,
    onOpen: openConfirmModal
  } = useDisclosure();

  useEffect(() => {
    fetchScheduledTransfers();
  }, [reqData.pageNumber]);

  const fetchScheduledTransfers = () => {
    const params = {
      page: reqData.pageNumber,
      page_size: reqData.pageSize
    };
    dispatch(getScheduledTransfers(params));
  };

  const handlePageChange = (page) => {
    dispatch(updateLibraScheduledTransfersReqData({
      ...reqData,
      pageNumber: page - 1
    }));
  };

  // Handle field changes to track selected beneficiaries and form data
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    if (fieldName === 'beneficiaryIds') {
      setSelectedBeneficiaryIds(Array.isArray(value) ? value : []);
    }
  };

  // Reset state when modal closes
  const handleCloseCreateModal = () => {
    setSelectedBeneficiaryIds([]);
    setFormData({});
    closeCreateModal();
  };

  const handleCloseEditModal = () => {
    setEditingTransfer(null);
    setSelectedBeneficiaryIds([]);
    setFormData({});
    closeEditModal();
  };

  // Called when create form is submitted - shows confirmation modal
  const handleCreateFormSubmit = (formData) => {
    setPendingFormData(formData);
    setConfirmAction('create');
    closeCreateModal();
    openConfirmModal();
  };

  // Called when edit form is submitted - shows confirmation modal
  const handleEditFormSubmit = (formData) => {
    setPendingFormData(formData);
    setPendingScheduledTransferId(editingTransfer?.externalId);
    setConfirmAction('edit');
    closeEditModal();
    openConfirmModal();
  };

  // Called when confirmation is confirmed
  const handleConfirmAction = () => {
    if (!pendingFormData) return;

    const reqData = buildScheduledTransferRequestData(pendingFormData);

    if (confirmAction === 'create') {
      dispatch(
        createScheduledTransfer(reqData, () => {
          closeConfirmModal();
          setPendingFormData(null);
          setConfirmAction(null);
          setSelectedBeneficiaryIds([]);
          setFormData({});
          fetchScheduledTransfers();
        })
      );
    } else if (confirmAction === 'edit') {
      dispatch(
        updateScheduledTransfer(pendingScheduledTransferId, reqData, () => {
          closeConfirmModal();
          setPendingFormData(null);
          setPendingScheduledTransferId(null);
          setConfirmAction(null);
          setEditingTransfer(null);
          setSelectedBeneficiaryIds([]);
          setFormData({});
          fetchScheduledTransfers();
        })
      );
    }
  };

  // Called when confirmation is cancelled
  const handleCancelConfirm = () => {
    closeConfirmModal();
    setPendingFormData(null);
    setPendingScheduledTransferId(null);
    setConfirmAction(null);
  };

  const handleOpenEditModal = (transfer) => {
    setEditingTransfer(transfer);

    // Pre-populate formData for edit mode
    const strategy = transfer.fundTransferStrategy;
    let beneficiaryIds = [];

    if (strategy) {
      if (strategy.strategyType === 'PERCENTAGE_SPLIT' && strategy.bankAccountSplits) {
        beneficiaryIds = strategy.bankAccountSplits.map(split => split.bankAccountId);
      } else if (strategy.strategyType === 'SKIPPABLE_RATIO' && strategy.bankAccountRatios) {
        beneficiaryIds = strategy.bankAccountRatios.map(ratio => ratio.bankAccountId);
      }
    }

    setSelectedBeneficiaryIds(beneficiaryIds);

    // Build initial form data
    const initialFormData = {
      scheduledTransferType: transfer.scheduledTransferType,
      accountId: transfer.accountId,
      strategyType: strategy?.strategyType || 'PERCENTAGE_SPLIT',
      amountType: transfer.amountSpec?.amountType || 'FIXED',
      amount: transfer.amountSpec?.amount?.toString() || '',
      beneficiaryIds: beneficiaryIds,
      executionTime: transfer.executionTime ? new Date(transfer.executionTime).toISOString().slice(0, 16) : '',
      cronExpression: transfer.cronExpression || ''
    };

    // Add split values
    if (strategy?.strategyType === 'PERCENTAGE_SPLIT' && strategy.bankAccountSplits) {
      strategy.bankAccountSplits.forEach(split => {
        initialFormData[`split_${split.bankAccountId}`] = split.percentage?.toString() || '';
      });
    } else if (strategy?.strategyType === 'SKIPPABLE_RATIO' && strategy.bankAccountRatios) {
      strategy.bankAccountRatios.forEach(ratio => {
        initialFormData[`split_${ratio.bankAccountId}`] = ratio.ratio?.toString() || '';
      });
    }

    setFormData(initialFormData);
    openEditModal();
  };

  // Get dynamic fields with beneficiary split configuration
  const createScheduledTransferFields = useMemo(() => {
    const baseFields = getScheduledTransferFields(null, null, libraConfigs);

    // Add values from formData to base fields for edit mode
    const fieldsWithValues = baseFields.map(field => ({
      ...field,
      value: formData[field.name] !== undefined ? formData[field.name] : field.value
    }));

    // If beneficiaries are selected, add individual percentage/ratio fields for each
    if (selectedBeneficiaryIds.length > 0) {
      const beneficiariesMap = {};
      libraConfigs?.corporateBeneficiaries?.forEach(beneficiary => {
        beneficiariesMap[beneficiary.corporateBeneficiaryId] = {
          name: beneficiary.accountName || beneficiary.label,
          id: beneficiary.corporateBeneficiaryId
        };
      });

      const strategyType = formData.strategyType || 'PERCENTAGE_SPLIT';
      const strategyLabel = strategyType === 'PERCENTAGE_SPLIT' ? 'Percentage' : 'Ratio';

      // Add individual fields for each selected beneficiary
      const dynamicFields = selectedBeneficiaryIds.map((beneficiaryId) => {
        const beneficiaryInfo = beneficiariesMap[beneficiaryId];
        const label = beneficiaryInfo
          ? `${beneficiaryInfo.name} - ${strategyLabel}`
          : `${beneficiaryId} - ${strategyLabel}`;

        return {
          name: `split_${beneficiaryId}`,
          label: label,
          fieldType: INPUT_FIELD_TYPE.INPUT,
          inputType: INPUT_TYPE.NUMBER,
          isRequired: true,
          isEditable: true,
          placeholder: strategyType === 'PERCENTAGE_SPLIT' ? 'Enter percentage (0-100)' : 'Enter ratio (e.g., 1, 2, 3)',
          value: formData[`split_${beneficiaryId}`] || ''
        };
      });

      const allFields = [...fieldsWithValues, ...dynamicFields];

      // Filter fields based on dependsOn conditions
      return allFields.filter(field => {
        if (!field.dependsOn) return true;
        return formData[field.dependsOn.field] === field.dependsOn.value;
      });
    }

    // Filter base fields based on dependsOn conditions
    return fieldsWithValues.filter(field => {
      if (!field.dependsOn) return true;
      return formData[field.dependsOn.field] === field.dependsOn.value;
    });
  }, [libraConfigs, selectedBeneficiaryIds, formData]);

  return (
    <Box position={'relative'} overflowX={'hidden'} flex={1}>
      <ModalLayout
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        size="xl"
        scrollBehavior="inside"
      >
        <DynamicInputModal
          title="Create Scheduled Transfer"
          onClose={handleCloseCreateModal}
          onSubmit={handleCreateFormSubmit}
          fields={createScheduledTransferFields}
          isLoading={createScheduledTransferState.isLoading}
          onFieldChange={handleFieldChange}
        />
      </ModalLayout>

      <ModalLayout
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        size="xl"
        scrollBehavior="inside"
      >
        <DynamicInputModal
          title="Edit Scheduled Transfer"
          onClose={handleCloseEditModal}
          onSubmit={handleEditFormSubmit}
          fields={createScheduledTransferFields}
          isLoading={updateScheduledTransferState.isLoading}
          onFieldChange={handleFieldChange}
        />
      </ModalLayout>

      {/* Confirmation Modal */}
      <ModalLayout
        isOpen={isConfirmModalOpen}
        onClose={handleCancelConfirm}
        size="md"
      >
        <ConfirmModal
          handleSubmit={handleConfirmAction}
          isLoading={confirmAction === 'create' ? createScheduledTransferState.isLoading : updateScheduledTransferState.isLoading}
          prompt={confirmAction === 'create'
            ? "Are you sure you want to create this scheduled transfer?"
            : "Are you sure you want to update this scheduled transfer?"}
        />
      </ModalLayout>

      <VStack align="stretch" spacing={4}>
        {/* Header with heading and actions */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="lg" color="colorPrimary">
            Scheduled Transfers
          </Heading>
          {isBackofficeManager && (
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              size="sm"
              onClick={openCreateModal}
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
            >
              Create Scheduled Transfer
            </Button>
          )}
        </Flex>
        {!scheduledTransfers.isLoading && scheduledTransfers?.data?.items?.length > 0 && (
          <Flex mt={4}>
            <Pagination
              totalPages={scheduledTransfers?.data?.maxPages || 1}
              currentPage={reqData.pageNumber + 1}
              onPageChange={handlePageChange}
            />
          </Flex>
        )}
        {/* Scheduled Transfers Table */}
        <DataTable
          data={scheduledTransfers?.data?.items || []}
          columns={getScheduledTransfersColumns(onItemClick, selectedItem)}
          isLoading={scheduledTransfers.isLoading}
          noDataFoundMessage="No scheduled transfers found."
          onRowClick={(scheduledTransfer) => {
            const sidebarActions = [
              <Button
                key="edit"
                size="md"
                colorScheme="blue"
                leftIcon={<MdEdit />}
                w="full"
                onClick={() => handleOpenEditModal(scheduledTransfer)}
                isLoading={updateScheduledTransferState.isLoading}
              >
                Edit
              </Button>
            ];
            onItemClick(scheduledTransfer, sidebarActions);
          }}
          selectedRow={selectedItem}
          rowKeyField="externalId"
        />
      </VStack>
    </Box>
  );
};
